import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { signOut } from '@/entities/session/api/sign-out';
import { ConfirmButton } from '@/shared/components/confirm-button';
import { useTranslation } from '@/shared/i18n/use-translation';
import { usePendingCount } from '@/shared/sync/use-pending-count';

type SignOutConfirmProps = {
  readonly trigger: ReactElement;
};

/** Sign out on this device after confirming; warns when changes have not reached the server. */
export function SignOutConfirm({ trigger }: SignOutConfirmProps) {
  const navigate = useNavigate();
  const pending = usePendingCount() ?? 0;
  const { t, tn } = useTranslation();

  async function leave() {
    await signOut();
    navigate('/sign-in', { replace: true });
  }

  return (
    <ConfirmButton
      title={t('Sign out on this device?')}
      description={
        pending > 0
          ? tn(
              pending,
              '{count} change has not reached the server and will be lost. Sync first if you can.',
              '{count} changes have not reached the server and will be lost. Sync first if you can.',
            )
          : t("This device's local copy will be erased. The data stays on the server.")
      }
      confirmLabel={t('Sign out')}
      onConfirm={leave}
      trigger={trigger}
    />
  );
}
